import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit, Trash2, Filter, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Landmark, Wallet, PieChart as PieChartIcon, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../utils/currency';
import IncomeModal from '../components/modals/IncomeModal';
import AccountModal from '../components/modals/AccountModal';
import ExpenseModal from '../components/modals/ExpenseModal';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const ITEMS_PER_PAGE = 10;

const Financials = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('income');
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modals
  const [incomeModalOpen, setIncomeModalOpen] = useState(false);
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Filters & Search
  const [incomeStatusFilter, setIncomeStatusFilter] = useState('all');
  const [incomeSearchQuery, setIncomeSearchQuery] = useState('');
  const [incomePage, setIncomePage] = useState(1);

  const [expenseCategoryFilter, setExpenseCategoryFilter] = useState('all');
  const [expenseSearchQuery, setExpenseSearchQuery] = useState('');
  const [expensePage, setExpensePage] = useState(1);

  const [accountsSearchQuery, setAccountsSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  useEffect(() => {
    if (!loading && analytics) {
      const hasSeenTour = localStorage.getItem('hasSeenFinancialsTour');
      if (!hasSeenTour) {
        const driverObj = driver({
          showProgress: true,
          steps: [
            {
              element: '[data-testid="financial-summary"]',
              popover: {
                title: 'Financial Summary',
                description: 'Overview of your total income, expenses, profit, and realization rate.',
                side: "bottom",
                align: 'start'
              }
            },
            {
              element: '[data-testid="financial-tabs"]',
              popover: {
                title: 'Financial Management',
                description: 'Switch between Income tracking, Expenses, Bank Accounts, and deep Analytics.',
                side: "top",
                align: 'start'
              }
            },
            {
              element: '[data-testid="financial-actions"]',
              popover: {
                title: 'Quick Actions',
                description: 'Add new income, record expenses, or create new accounts easily.',
                side: "left",
                align: 'start'
              }
            }
          ],
          onDestroyed: () => {
            localStorage.setItem('hasSeenFinancialsTour', 'true');
          }
        });
        setTimeout(() => driverObj.drive(), 1000);
      }
    }
  }, [loading, analytics]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [incomeRes, expenseRes, accountRes, projectsRes, analyticsRes] = await Promise.all([
        apiClient.get('/income'),
        apiClient.get('/expenses'),
        apiClient.get('/accounts'),
        apiClient.get('/projects'),
        apiClient.get('/analytics/complete')
      ]);
      setIncomeRecords(incomeRes.data.items || incomeRes.data);
      setExpenses(expenseRes.data.items || expenseRes.data);
      setAccounts(accountRes.data);
      setProjects(projectsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      if (activeTab === 'income') await apiClient.delete(`/income/${itemToDelete.income_id}`);
      else if (activeTab === 'expenses') await apiClient.delete(`/expenses/${itemToDelete.expense_id}`);
      else if (activeTab === 'accounts') await apiClient.delete(`/accounts/${itemToDelete.account_id}`);

      toast.success('Deleted successfully');
      fetchData();
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-amber-100 text-amber-800',
      'received': 'bg-emerald-100 text-emerald-800',
      'overdue': 'bg-rose-100 text-rose-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  // Filtering Logic
  const filteredIncome = incomeRecords.filter(record => {
    const matchesStatus = incomeStatusFilter === 'all' || record.payment_status === incomeStatusFilter;
    const matchesSearch = record.notes?.toLowerCase().includes(incomeSearchQuery.toLowerCase()) ||
                         record.amount.toString().includes(incomeSearchQuery);
    return matchesStatus && matchesSearch;
  });

  const filteredExpenses = expenses.filter(expense => {
    const matchesCategory = expenseCategoryFilter === 'all' || expense.category === expenseCategoryFilter;
    const matchesSearch = expense.notes?.toLowerCase().includes(expenseSearchQuery.toLowerCase()) ||
                         expense.amount.toString().includes(expenseSearchQuery);
    return matchesCategory && matchesSearch;
  });

  // Pagination Logic
  const paginatedIncome = filteredIncome.slice((incomePage - 1) * ITEMS_PER_PAGE, incomePage * ITEMS_PER_PAGE);
  const paginatedExpenses = filteredExpenses.slice((expensePage - 1) * ITEMS_PER_PAGE, expensePage * ITEMS_PER_PAGE);

  const totalIncomePages = Math.ceil(filteredIncome.length / ITEMS_PER_PAGE);
  const totalExpensePages = Math.ceil(filteredExpenses.length / ITEMS_PER_PAGE);

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Financial Hub</h1>
          <p className="text-slate-600 mt-1">Manage income, expenses, and accounts</p>
        </div>
        <div className="flex gap-2" data-testid="financial-actions">
          {activeTab === 'income' && (
            <Button onClick={() => { setSelectedItem(null); setIncomeModalOpen(true); }} className="bg-indigo-600">
              <Plus className="h-4 w-4 mr-2" /> Add Income
            </Button>
          )}
          {activeTab === 'expenses' && (
            <Button onClick={() => { setSelectedItem(null); setExpenseModalOpen(true); }} className="bg-rose-600 hover:bg-rose-700">
              <Plus className="h-4 w-4 mr-2" /> Record Expense
            </Button>
          )}
          {activeTab === 'accounts' && (
            <Button onClick={() => { setSelectedItem(null); setAccountModalOpen(true); }} variant="outline" className="border-indigo-200 text-indigo-700">
              <Landmark className="h-4 w-4 mr-2" /> New Account
            </Button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="financial-summary">
        <Card className="border-slate-100 bg-emerald-50/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <TrendingUp className="h-4 w-4" />
              <p className="text-xs font-semibold uppercase tracking-wider">Total Income</p>
            </div>
            <p className="text-2xl font-bold text-slate-900">{getCurrencySymbol(user?.currency)}{analytics?.financial?.received_income?.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-100 bg-rose-50/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-rose-600 mb-1">
              <TrendingDown className="h-4 w-4" />
              <p className="text-xs font-semibold uppercase tracking-wider">Total Expenses</p>
            </div>
            <p className="text-2xl font-bold text-slate-900">{getCurrencySymbol(user?.currency)}{analytics?.financial?.total_expenses?.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-100 bg-indigo-50/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-indigo-600 mb-1">
              <Wallet className="h-4 w-4" />
              <p className="text-xs font-semibold uppercase tracking-wider">Net Profit</p>
            </div>
            <p className="text-2xl font-bold text-slate-900">{getCurrencySymbol(user?.currency)}{analytics?.financial?.net_profit?.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-100 bg-amber-50/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <PieChartIcon className="h-4 w-4" />
              <p className="text-xs font-semibold uppercase tracking-wider">Realization</p>
            </div>
            <p className="text-2xl font-bold text-slate-900">{analytics?.financial?.income_realization_rate}%</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="financial-tabs">
        <TabsList className="bg-slate-100 p-1 w-full lg:w-auto">
          <TabsTrigger value="income" className="px-6">Income</TabsTrigger>
          <TabsTrigger value="expenses" className="px-6">Expenses</TabsTrigger>
          <TabsTrigger value="accounts" className="px-6">Accounts</TabsTrigger>
          <TabsTrigger value="analytics" className="px-6">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="income" className="mt-6">
            <div className="mb-4 flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                        placeholder="Search income records..."
                        value={incomeSearchQuery}
                        onChange={(e) => { setIncomeSearchQuery(e.target.value); setIncomePage(1); }}
                        className="pl-10"
                    />
                </div>
                <Select value={incomeStatusFilter} onValueChange={(val) => { setIncomeStatusFilter(val); setIncomePage(1); }}>
                    <SelectTrigger className="w-full md:w-48">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="received">Received</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <Card className="border-slate-100">
                <CardHeader>
                    <CardTitle>Income Tracking</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {paginatedIncome.length === 0 ? (
                            <div className="text-center py-12 text-slate-400">No income records found.</div>
                        ) : (
                            paginatedIncome.map(record => (
                                <div key={record.income_id} className="flex items-center justify-between p-4 border rounded-lg hover:border-indigo-200 transition-all">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-lg font-bold">{getCurrencySymbol(user?.currency)}{record.amount.toLocaleString()}</span>
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-bold ${getStatusColor(record.payment_status)}`}>
                                                {record.payment_status}
                                            </span>
                                        </div>
                                        <p className="text-sm text-slate-500">{record.notes || 'No description'}</p>
                                        <p className="text-[10px] text-slate-400 mt-1">
                                            {record.payment_status === 'received' ? `Received: ${record.received_date}` : `Expected: ${record.expected_date}`}
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="ghost" size="sm" onClick={() => { setSelectedItem(record); setIncomeModalOpen(true); }}><Edit className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="sm" onClick={() => { setItemToDelete(record); setDeleteDialogOpen(true); }} className="text-rose-500"><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {totalIncomePages > 1 && (
                        <div className="flex items-center justify-between mt-6 pt-4 border-t">
                            <p className="text-sm text-slate-500">Showing {(incomePage - 1) * ITEMS_PER_PAGE + 1} - {Math.min(incomePage * ITEMS_PER_PAGE, filteredIncome.length)} of {filteredIncome.length}</p>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => setIncomePage(p => Math.max(1, p - 1))} disabled={incomePage === 1}>
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setIncomePage(p => Math.min(totalIncomePages, p + 1))} disabled={incomePage === totalIncomePages}>
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="expenses" className="mt-6">
            <div className="mb-4 flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                        placeholder="Search expenses..."
                        value={expenseSearchQuery}
                        onChange={(e) => { setExpenseSearchQuery(e.target.value); setExpensePage(1); }}
                        className="pl-10"
                    />
                </div>
                <Select value={expenseCategoryFilter} onValueChange={(val) => { setExpenseCategoryFilter(val); setExpensePage(1); }}>
                    <SelectTrigger className="w-full md:w-48">
                        <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        <SelectItem value="Operational">Operational</SelectItem>
                        <SelectItem value="Marketing">Marketing</SelectItem>
                        <SelectItem value="SaaS">SaaS</SelectItem>
                        <SelectItem value="Payroll">Payroll</SelectItem>
                        <SelectItem value="Travel">Travel</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <Card className="border-slate-100">
                <CardHeader>
                    <CardTitle>Business Expenses</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {paginatedExpenses.length === 0 ? (
                            <div className="text-center py-12 text-slate-400">No expenses recorded.</div>
                        ) : (
                            paginatedExpenses.map(expense => (
                                <div key={expense.expense_id} className="flex items-center justify-between p-4 border rounded-lg hover:border-rose-200 transition-all">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-lg font-bold text-rose-600">{getCurrencySymbol(user?.currency)}{expense.amount.toLocaleString()}</span>
                                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase font-bold">
                                                {expense.category}
                                            </span>
                                        </div>
                                        <p className="text-sm text-slate-500">{expense.notes || 'No description'}</p>
                                        <p className="text-[10px] text-slate-400 mt-1">Date: {expense.date}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="ghost" size="sm" onClick={() => { setItemToDelete(expense); setDeleteDialogOpen(true); }} className="text-rose-500"><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {totalExpensePages > 1 && (
                        <div className="flex items-center justify-between mt-6 pt-4 border-t">
                            <p className="text-sm text-slate-500">Showing {(expensePage - 1) * ITEMS_PER_PAGE + 1} - {Math.min(expensePage * ITEMS_PER_PAGE, filteredExpenses.length)} of {filteredExpenses.length}</p>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => setExpensePage(p => Math.max(1, p - 1))} disabled={expensePage === 1}>
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setExpensePage(p => Math.min(totalExpensePages, p + 1))} disabled={expensePage === totalExpensePages}>
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="accounts" className="mt-6">
            <div className="mb-4">
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                        placeholder="Search accounts..."
                        value={accountsSearchQuery}
                        onChange={(e) => setAccountsSearchQuery(e.target.value)}
                        className="pl-10"
                    />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {accounts.filter(acc => acc.name.toLowerCase().includes(accountsSearchQuery.toLowerCase())).map(account => (
                    <Card key={account.account_id} className={`border-slate-200 relative overflow-hidden ${account.is_default ? 'ring-2 ring-indigo-500' : ''}`}>
                        {account.is_default && <div className="absolute top-0 right-0 bg-indigo-500 text-white text-[10px] px-2 py-0.5 font-bold rounded-bl">DEFAULT</div>}
                        <CardHeader className="pb-2">
                            <CardTitle className="text-lg flex items-center gap-2">
                                {account.type === 'bank' ? <Landmark className="h-5 w-5 text-indigo-600" /> : <Wallet className="h-5 w-5 text-emerald-600" />}
                                {account.name}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-2xl font-bold mb-4">{getCurrencySymbol(user?.currency)}{account.balance.toLocaleString()}</p>
                            <div className="flex justify-between items-center text-[10px] text-slate-400 uppercase font-bold tracking-wider">
                                <span>{account.type} • {account.currency}</span>
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setSelectedItem(account); setAccountModalOpen(true); }}><Edit className="h-3 w-3" /></Button>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-rose-500" onClick={() => { setItemToDelete(account); setDeleteDialogOpen(true); }}><Trash2 className="h-3 w-3" /></Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-slate-100">
                    <CardHeader><CardTitle>Cash Flow (Income vs Expense)</CardTitle></CardHeader>
                    <CardContent className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={analytics?.cash_flow || []}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip formatter={(value) => [`${getCurrencySymbol(user?.currency)}${value}`, '']} />
                                <Legend />
                                <Bar dataKey="income" fill="#10B981" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="expense" fill="#EF4444" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                <Card className="border-slate-100">
                    <CardHeader><CardTitle>Expense Breakdown</CardTitle></CardHeader>
                    <CardContent className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={analytics?.financial?.expense_breakdown || [
                                        { name: 'Operational', value: 400 },
                                        { name: 'Marketing', value: 300 },
                                        { name: 'SaaS', value: 300 },
                                        { name: 'Other', value: 200 },
                                    ]}
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {COLORS.map((color, index) => <Cell key={`cell-${index}`} fill={color} />)}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>
        </TabsContent>
      </Tabs>

      <IncomeModal open={incomeModalOpen} onClose={() => setIncomeModalOpen(false)} income={selectedItem} onSuccess={fetchData} />
      <AccountModal open={accountModalOpen} onClose={() => setAccountModalOpen(false)} account={selectedItem} onSuccess={fetchData} />
      <ExpenseModal open={expenseModalOpen} onClose={() => setExpenseModalOpen(false)} expense={selectedItem} onSuccess={fetchData} />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this record. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Financials;
